import { createOpaqueValue, getOpaqueValue } from "./opaqueValue";

/**
 * Conversation.id uses the following set: `@:._-`
 */
const SEPARATOR = "|";
//const ID_COMPONENTS_NUMBER = 2;

export type NodeIDComponents = [
    typename: string,
    ...ids: string[]
];

export type NodeID = string;

/**
 * Encodes `components` into a single string. The components should hold the
 * metadata necessary to be able to refetch the data from the upstream source
 * and should lead to a globally unique value.
 *
 * Typically the GraphQL typename and the upstream `id` value are enough, but
 * in some cases extra data may be required for the resolver to know how to
 * retrieve the data.
 *
 * @important
 * The user of this data MUST NOT make any assumptions about the value. If the
 * user needs an internal id reference to pass to a service outside of the
 * context of GraphQL, that id should be made available on the GraphQL type
 * using a different field name than `id`.
 *
 * @note
 * In development mode, this string is base64 encoded to avoid the user of this
 * data making any assumptions about the value.
 */
export function toNodeID(components: NodeIDComponents): NodeID {
    return createOpaqueValue(components.join(SEPARATOR)) as NodeID;
}

/**
 * Decodes `id` back into a set of components.
 *
 * @see {toNodeID}
 */
export function fromNodeID(id: NodeID): NodeIDComponents {
    const components = getOpaqueValue(id).split(SEPARATOR);
    return components as NodeIDComponents;
}

/**
 * Encodes a mailbox identifier into a Node ID for types implementing OneGQL_HostAppAccount interface.
 *
 * IMPORTANT: This function is ONLY for encoding account IDs for types that implement OneGQL_HostAppAccount.
 * Do not use this function for other types or interfaces.
 *
 * Currently, PeopleApp_Account is the ONLY concrete type that implements OneGQL_HostAppAccount.
 * The optional `typeName` parameter allows for future extensibility when other concrete implementations
 * of OneGQL_HostAppAccount are added (e.g., TeamsApp_Account, CalendarApp_Account).
 *
 * @param mailboxInfoIdentifier The mailbox identifier to encode
 * @param typeName Optional concrete type name. Defaults to 'PeopleApp_Account' (currently the only implementation)
 * @returns A Node ID for the specified concrete type that implements OneGQL_HostAppAccount
 */
export function encodeAccountIdToNodeID(mailboxInfoIdentifier: string, typeName?: string): NodeID {
    return toNodeID([typeName ?? "PeopleApp_Account", mailboxInfoIdentifier]);
}
